import type { NextPage } from "next";
import React, { useEffect, useState } from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useDispatch } from "react-redux";
import { useTranslation } from "next-i18next";
import {
  getClassroomById,
  getClassroomQuestsByClassroomSlug,
  getIsAcademyClassroom,
  getMetadata,
  sharedServerSide,
} from "../../helpers";
import { loadCurrentpage } from "../../store/slices/app-slice";
import { Locale } from "../../src/types";
import Hero from "../../components/Classroom/Hero";
import { IClassroom, IPresentationQuest } from "../../src/interfaces";
import Login from "../../components/Login";
import Quests from "../../components/Classroom/Quests";
import AcademyArea from "../../components/AcademyArea";
import SharePurpleArea from "../../components/SharePurpleArea";
import WhatsNext from "../../components/Classroom/WhatsNext";

export interface ClassroomProps {
  classroom: IClassroom;
  quests: IPresentationQuest[];
  serverDateJSON: Date;
  isAcademyClassroom: boolean;
}

export const getServerSideProps = sharedServerSide(
  async ({ locale, query }) => {
    const currentLocale = locale as Locale;
    const serverDateJSON = new Date().toJSON();
    const slug = query.title;

    const [classroom, quests, metas, isAcademyClassroom] = await Promise.all([
      getClassroomById(currentLocale, slug),
      getClassroomQuestsByClassroomSlug(currentLocale, slug),
      getMetadata("classroom", currentLocale),
      getIsAcademyClassroom(currentLocale, "Academy page", slug),
    ]);

    if (!classroom) {
      return {
        notFound: true, // redirects to 404 page
      };
    }

    return {
      props: {
        classroom,
        privateClassroom: classroom.isPrivate,
        quests,
        isAcademyClassroom,
        metas: classroom.isPrivate ? null : metas,
        serverDateJSON,
        ...(await serverSideTranslations(currentLocale, [
          "common",
          "classroom",
        ])),
      },
    };
  }
);

const Classroom: NextPage<ClassroomProps> = function ({
  classroom,
  quests,
  serverDateJSON,
  isAcademyClassroom,
}) {
  const { t } = useTranslation("classroom");
  const serverDate = new Date(serverDateJSON);
  const dispatch = useDispatch();
  const [modalIsOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    dispatch(loadCurrentpage("Classroom"));
  }, []);

  const handleLogin = async () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Hero
        classroom={classroom}
        handleLogin={handleLogin}
        isAcademyClassroom={isAcademyClassroom}
      />
      <Quests
        title={classroom.questsTitle}
        quests={quests}
        serverDate={serverDate}
      />
      <WhatsNext title={t("whatsNextTitle")} data={classroom.whatsNext} />
      <AcademyArea />
      <SharePurpleArea />

      <Login isOpen={modalIsOpen} close={closeModal} />
    </>
  );
};

export default Classroom;
